import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { colorPalette } from "../styles/theme";
import { SUPER_ADMIN_PROJECTS } from "./adminProjects";
import OverviewView from "./OverviewView";
import ParticipantsView from "./ParticipantsView";
import ReportView from "./ReportView";
import SopGuideDrawer from "./SopGuideDrawer";

/**
 * 소속 관리자 샘플 화면 (샘플 계정 전용 전체 레이아웃)
 * - 좌측 사이드바(로고 / 조회 범위 / 메뉴 / 사용자 정보) + 상단 헤더 + 본문으로 구성됩니다.
 * - 로그인 직후 첫 화면으로 "종합현황" 메뉴가 노출됩니다.
 * - 공용 Sidebar/상단 타이틀 바를 쓰지 않으므로 다른 계정 화면에는 영향이 없습니다.
 *
 * 권한별 사이드바 차이:
 *  - 슈퍼 관리자: 기관 전환기(전체 기관 / 기관·사업 선택) + "시스템 관리" 메뉴 노출
 *  - 기관 관리자: 본인 기관으로 조회 범위 고정(전환 불가), 기관 운영 메뉴만 노출
 *  (실제 데이터 범위 제한은 서버에서 처리하며, 화면은 노출 범위만 구분합니다.)
 */

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;
// 메인 섹션(상단 바 아래 본문) 배경색 — 샘플 화면 전용 (공용 colorPalette.background는 다른 화면에서 쓰므로 그대로 둠)
const MAIN_BACKGROUND = "#F0F6FB";
// 사이드바 접힘 상태 기억용 (브라우저별 편의 설정)
const SIDEBAR_COLLAPSED_STORAGE_KEY = "mallang-sample-sidebar-collapsed";

/** 저장된 접힘 상태가 없으면 화면이 좁을 때(1280px 미만) 접힌 상태로 시작 */
const getInitialCollapsed = () => {
  try {
    const saved = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (saved !== null) return saved === "true";
  } catch {
    // 저장소 접근 불가(시크릿 모드 등) 시 화면 폭 기준으로만 판단
  }
  return window.innerWidth < 1280;
};

const ROLE = { SUPER: "super", ORG: "org" };

const ROLE_LABEL = {
  [ROLE.SUPER]: "슈퍼 관리자",
  [ROLE.ORG]: "기관 관리자",
};

// 기관 운영 메뉴 — 모든 관리자 공통
const ORG_MENUS = [
  {
    key: "overview",
    label: "종합현황",
    description: "기간별 전체 현황과 위험 대상자를 한눈에 확인합니다.",
    Icon: SpaceDashboardOutlinedIcon,
  },
  {
    key: "participants",
    label: "참가자 관리",
    description: "참가자별 검사 이력과 상태를 상세하게 확인합니다.",
    Icon: PersonSearchOutlinedIcon,
  },
  {
    key: "report",
    label: "기관 통계 리포트",
    description: "기관 단위 통계를 리포트 형태로 확인합니다.",
    Icon: AssessmentOutlinedIcon,
  },
];

// 시스템 관리 메뉴 — 슈퍼 관리자 전용
const SYSTEM_MENUS = [
  {
    key: "organizations",
    label: "기관 관리",
    description: "등록된 기관과 사업(코드)을 추가·수정합니다.",
    Icon: DomainAddOutlinedIcon,
  },
  {
    key: "admins",
    label: "관리자 계정 관리",
    description: "기관별 관리자 계정과 권한을 관리합니다.",
    Icon: ManageAccountsOutlinedIcon,
  },
  {
    key: "inquiry",
    label: "문의 내역",
    description: "앱 사용자 문의를 확인하고 답변합니다.",
    Icon: QuestionAnswerOutlinedIcon,
  },
];

const ALL_MENUS = [...ORG_MENUS, ...SYSTEM_MENUS];

// 슈퍼 관리자 기관 전환기 목록 — 문의사항 코드는 기관이 아니라 "문의 내역" 메뉴로 분리되어 제외
const SWITCHABLE_PROJECTS = SUPER_ADMIN_PROJECTS.filter((p) => p.code !== "GARAM-INQUIRY");

/** 기관명 기준으로 사업 목록을 묶음 */
const groupByOrganization = (projects) => {
  const groups = new Map();
  projects.forEach((project) => {
    const list = groups.get(project.organization) ?? [];
    list.push(project);
    groups.set(project.organization, list);
  });
  return Array.from(groups.entries()).map(([organization, items]) => ({ organization, items }));
};

const ORGANIZATION_COUNT = groupByOrganization(SWITCHABLE_PROJECTS).length;

const formatToday = () => {
  const now = new Date();
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}.${mm}.${dd} (${days[now.getDay()]})`;
};

// GARAM_02.svg(viewBox 2560x1440)는 심볼 주변 여백이 넓어서, 심볼 영역만 잘라내기 위한 좌표
const LOGO_SVG = { width: 2560, height: 1440 };
const LOGO_SYMBOL_CROP = { x: 750, y: 175, width: 1060, height: 1090 };

/** 가람마인드 심볼 — 원본 SVG를 그대로 쓰되 컨테이너로 심볼 부분만 보이게 자름 */
const LogoSymbol = ({ size }) => {
  const scale = size / LOGO_SYMBOL_CROP.height;
  return (
    <Box
      sx={{
        position: "relative",
        width: LOGO_SYMBOL_CROP.width * scale,
        height: size,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={`${process.env.PUBLIC_URL}/GARAM_02.svg`}
        alt="GARAM MIND"
        sx={{
          position: "absolute",
          left: -LOGO_SYMBOL_CROP.x * scale,
          top: -LOGO_SYMBOL_CROP.y * scale,
          width: LOGO_SVG.width * scale,
          height: LOGO_SVG.height * scale,
          maxWidth: "none",
          display: "block",
        }}
      />
    </Box>
  );
};

/** 사이드바 섹션 제목 — 접힌 상태에서는 제목 대신 짧은 구분선만 표시 */
const SectionTitle = ({ children, collapsed }) =>
  collapsed ? (
    <Box sx={{ mx: "auto", mb: 1, width: 24, height: "1px", backgroundColor: colorPalette.border }} />
  ) : (
    <Typography
      sx={{
        px: 1.5,
        mb: 0.75,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.12em",
        color: colorPalette.textSecondary,
        opacity: 0.75,
      }}
    >
      {children}
    </Typography>
  );

/** 기관 아이콘 박스 (전환기/고정 카드 공용) */
const OrgBadge = ({ highlighted }) => (
  <Box
    sx={{
      width: 32,
      height: 32,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 1.5,
      backgroundColor: highlighted ? colorPalette.primary : colorPalette.white,
      color: highlighted ? colorPalette.white : colorPalette.primary,
      border: highlighted ? "none" : `1px solid ${colorPalette.border}`,
    }}
  >
    <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />
  </Box>
);

/** 슈퍼 관리자 — 기관 전환기 (전체 기관 / 기관·사업 선택) */
const OrgSwitcher = ({ selectedProject, onSelect, collapsed }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [keyword, setKeyword] = useState("");

  const groups = useMemo(() => {
    const q = keyword.trim();
    const filtered = q
      ? SWITCHABLE_PROJECTS.filter(
          (p) => p.organization.includes(q) || p.name.includes(q) || p.code.toLowerCase().includes(q.toLowerCase()),
        )
      : SWITCHABLE_PROJECTS;
    return groupByOrganization(filtered);
  }, [keyword]);

  const handleClose = () => {
    setAnchorEl(null);
    setKeyword("");
  };

  const handleSelect = (project) => {
    onSelect(project);
    handleClose();
  };

  const optionSx = (selected) => ({
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1,
    px: 1.5,
    py: 1,
    borderRadius: 1.5,
    textAlign: "left",
    backgroundColor: selected ? colorPalette.activeBg : "transparent",
    "&:hover": { backgroundColor: selected ? colorPalette.activeBg : colorPalette.background },
  });

  const title = selectedProject ? selectedProject.organization : "전체 기관";
  const subtitle = selectedProject
    ? `${selectedProject.name} · ${selectedProject.code}`
    : `${ORGANIZATION_COUNT}개 기관 · ${SWITCHABLE_PROJECTS.length}개 사업 통합`;

  return (
    <>
      {collapsed ? (
        // 접힌 상태: 기관 아이콘만 표시, 마우스를 올리면 현재 조회 기관 안내
        <Tooltip title={`조회 기관: ${title} (${subtitle})`} placement="right">
          <ButtonBase
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ display: "flex", mx: "auto", borderRadius: 1.5 }}
          >
            <OrgBadge highlighted={!selectedProject} />
          </ButtonBase>
        </Tooltip>
      ) : (
        <ButtonBase
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1.25,
            p: 1.25,
            borderRadius: 2,
            textAlign: "left",
            border: `1px solid ${anchorEl ? colorPalette.primary : colorPalette.border}`,
            backgroundColor: colorPalette.background,
            transition: "border-color 0.15s ease",
            "&:hover": { borderColor: colorPalette.primary },
          }}
        >
          <OrgBadge highlighted={!selectedProject} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>
              {title}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11, color: colorPalette.textSecondary }}>
              {subtitle}
            </Typography>
          </Box>
          <UnfoldMoreIcon sx={{ fontSize: 18, color: colorPalette.textSecondary }} />
        </ButtonBase>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        // 접힌 상태에서는 아이콘 오른쪽으로 펼침
        anchorOrigin={collapsed ? { vertical: "top", horizontal: "right" } : { vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: collapsed ? 0 : 0.75,
              ml: collapsed ? 1.5 : 0,
              width: 300,
              maxHeight: 440,
              display: "flex",
              flexDirection: "column",
              borderRadius: 2,
              boxShadow: "0 12px 32px rgba(26, 31, 54, 0.14)",
            },
          },
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${colorPalette.border}` }}>
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder="기관명, 사업명, 코드 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: colorPalette.textSecondary }} />
                  </InputAdornment>
                ),
                sx: { fontSize: 13 },
              },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
          {!keyword.trim() && (
            <ButtonBase onClick={() => handleSelect(null)} sx={optionSx(!selectedProject)}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>
                  전체 기관
                </Typography>
                <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary }}>
                  모든 기관 데이터를 통합해서 봅니다
                </Typography>
              </Box>
              {!selectedProject && <CheckIcon sx={{ fontSize: 18, color: colorPalette.primary }} />}
            </ButtonBase>
          )}

          {groups.length === 0 ? (
            <Typography sx={{ py: 3, textAlign: "center", fontSize: 12, color: colorPalette.textSecondary }}>
              검색 결과가 없습니다.
            </Typography>
          ) : (
            groups.map((group) => (
              <Box key={group.organization} sx={{ mt: 1 }}>
                <Typography
                  sx={{ px: 1.5, py: 0.5, fontSize: 11, fontWeight: 700, color: colorPalette.textSecondary }}
                >
                  {group.organization}
                </Typography>
                {group.items.map((project) => {
                  const selected = selectedProject?.code === project.code;
                  return (
                    <ButtonBase key={project.code} onClick={() => handleSelect(project)} sx={optionSx(selected)}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: 13,
                            fontWeight: selected ? 700 : 500,
                            color: selected ? colorPalette.primary : colorPalette.textPrimary,
                          }}
                        >
                          {project.name}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 11, color: colorPalette.textSecondary }}>
                          {project.code}
                        </Typography>
                      </Box>
                      {selected && <CheckIcon sx={{ fontSize: 18, color: colorPalette.primary }} />}
                    </ButtonBase>
                  );
                })}
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
};

/** 기관 관리자 — 본인 기관으로 고정된 조회 범위 카드 (전환 불가) */
const OrgFixedCard = ({ me, collapsed }) =>
  collapsed ? (
    <Tooltip title={`소속 기관: ${me?.organization || "소속 기관"} (본 기관 데이터만 조회)`} placement="right">
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <OrgBadge />
      </Box>
    </Tooltip>
  ) : (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        p: 1.25,
        borderRadius: 2,
        border: `1px solid ${colorPalette.border}`,
        backgroundColor: colorPalette.background,
      }}
    >
      <OrgBadge />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>
          {me?.organization || "소속 기관"}
        </Typography>
        <Typography noWrap sx={{ fontSize: 11, color: colorPalette.textSecondary }}>
          {me?.code ? `코드 · ${me.code}` : "본 기관 데이터만 조회됩니다"}
        </Typography>
      </Box>
      <Tooltip title="본 기관 데이터만 조회할 수 있습니다">
        <LockOutlinedIcon sx={{ fontSize: 16, color: colorPalette.textSecondary }} />
      </Tooltip>
    </Box>
  );

/** 사이드바 메뉴 항목 — 접힌 상태에서는 아이콘만 표시하고 메뉴명은 툴팁으로 안내 */
const NavItem = ({ menu, selected, onClick, collapsed }) => {
  const { Icon } = menu;
  return (
    <Tooltip title={collapsed ? menu.label : ""} placement="right">
      <ButtonBase
        onClick={onClick}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1.5,
          px: 1.5,
          py: 1.1,
          borderRadius: 2,
          position: "relative",
          color: selected ? colorPalette.primary : colorPalette.textSecondary,
          backgroundColor: selected ? colorPalette.activeBg : "transparent",
          transition: "background-color 0.15s ease, color 0.15s ease",
          "&:hover": {
            backgroundColor: selected ? colorPalette.activeBg : colorPalette.background,
            color: selected ? colorPalette.primary : colorPalette.textPrimary,
          },
          // 선택된 메뉴 좌측 강조 바
          "&::before": {
            content: '""',
            position: "absolute",
            left: -12,
            top: 8,
            bottom: 8,
            width: 3,
            borderRadius: "0 3px 3px 0",
            backgroundColor: selected ? colorPalette.primary : "transparent",
          },
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
        {!collapsed && (
          <Typography noWrap sx={{ fontSize: 14, fontWeight: selected ? 700 : 500, color: "inherit" }}>
            {menu.label}
          </Typography>
        )}
      </ButtonBase>
    </Tooltip>
  );
};

/** 메뉴 섹션 (제목 + 항목 목록) */
const NavSection = ({ title, menus, menuKey, onMenuChange, collapsed }) => (
  <Box sx={{ mb: 2.5 }}>
    <SectionTitle collapsed={collapsed}>{title}</SectionTitle>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {menus.map((menu) => (
        <NavItem
          key={menu.key}
          menu={menu}
          collapsed={collapsed}
          selected={menuKey === menu.key}
          onClick={() => onMenuChange(menu.key)}
        />
      ))}
    </Box>
  </Box>
);

/** 좌측 사이드바 — 로고 / 조회 범위 / 메뉴 / 사용자 정보 */
const SampleSidebar = ({
  me,
  role,
  menuKey,
  onMenuChange,
  selectedProject,
  onProjectSelect,
  onChangePassword,
  onLogout,
  collapsed,
}) => {
  const displayName = me?.name || "관리자";
  const isSuper = role === ROLE.SUPER;

  return (
    <Box
      component="aside"
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        backgroundColor: colorPalette.white,
        borderRight: `1px solid ${colorPalette.border}`,
        transition: "width 0.2s ease",
      }}
    >
      {/* 로고 — 접힌 상태에서는 심볼만 표시 */}
      {collapsed ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 2.5, flexShrink: 0 }}>
          <LogoSymbol size={30} />
        </Box>
      ) : (
        <Box sx={{ px: 3, pt: 3, pb: 2.5, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LogoSymbol size={30} />
            <Typography
              noWrap
              sx={{ fontSize: 22, fontWeight: 800, color: colorPalette.textPrimary, lineHeight: 1, letterSpacing: "-0.5px" }}
            >
              말랑포켓
            </Typography>
          </Box>
          <Typography
            noWrap
            sx={{
              mt: 1,
              pl: "38px", // 심볼(30px) + 간격(8px) 만큼 들여 "말랑포켓" 글자와 시작선을 맞춤
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: colorPalette.textSecondary,
              opacity: 0.7,
            }}
          >
            ADMIN CONSOLE
          </Typography>
        </Box>
      )}

      {/* 조회 범위: 슈퍼 관리자는 기관 전환, 기관 관리자는 본인 기관 고정 */}
      <Box sx={{ px: 1.5, pb: 2, flexShrink: 0 }}>
        <SectionTitle collapsed={collapsed}>{isSuper ? "조회 기관" : "소속 기관"}</SectionTitle>
        {isSuper ? (
          <OrgSwitcher selectedProject={selectedProject} onSelect={onProjectSelect} collapsed={collapsed} />
        ) : (
          <OrgFixedCard me={me} collapsed={collapsed} />
        )}
      </Box>

      {/* 메뉴 */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 1.5, pt: 1 }}>
        <NavSection
          title="기관 운영"
          menus={ORG_MENUS}
          menuKey={menuKey}
          onMenuChange={onMenuChange}
          collapsed={collapsed}
        />
        {isSuper && (
          <NavSection
            title="시스템 관리"
            menus={SYSTEM_MENUS}
            menuKey={menuKey}
            onMenuChange={onMenuChange}
            collapsed={collapsed}
          />
        )}
      </Box>

      {/* 운영 가이드 안내 + 사용자 정보 — 접힌 상태에서는 아이콘만 세로로 배치 */}
      {collapsed ? (
        <Box
          sx={{
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            borderTop: `1px solid ${colorPalette.border}`,
            flexShrink: 0,
          }}
        >
          <Tooltip title="운영 가이드" placement="right">
            <IconButton size="small" sx={{ color: colorPalette.textSecondary }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={`${displayName} · ${ROLE_LABEL[role]}`} placement="right">
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: isSuper ? colorPalette.primary : colorPalette.activeBg,
                color: isSuper ? colorPalette.white : colorPalette.primary,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>
          <Tooltip title="비밀번호 변경" placement="right">
            <IconButton size="small" onClick={onChangePassword} sx={{ color: colorPalette.textSecondary }}>
              <LockResetOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="로그아웃" placement="right">
            <IconButton
              size="small"
              onClick={onLogout}
              sx={{ color: colorPalette.textSecondary, "&:hover": { color: colorPalette.error } }}
            >
              <LogoutOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ p: 1.5, borderTop: `1px solid ${colorPalette.border}`, flexShrink: 0 }}>
          <ButtonBase
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              mb: 1,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              textAlign: "left",
              "&:hover": { backgroundColor: colorPalette.background },
            }}
          >
            <HelpOutlineIcon sx={{ fontSize: 18, color: colorPalette.textSecondary }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, color: colorPalette.textSecondary, lineHeight: 1.4 }}>
                도움이 필요하신가요?
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.primary, lineHeight: 1.4 }}>
                운영 가이드
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ fontSize: 18, color: colorPalette.textSecondary }} />
          </ButtonBase>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1,
              borderRadius: 2,
              backgroundColor: colorPalette.background,
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                fontSize: 14,
                fontWeight: 700,
                bgcolor: isSuper ? colorPalette.primary : colorPalette.activeBg,
                color: isSuper ? colorPalette.white : colorPalette.primary,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: colorPalette.textPrimary }}>
                {displayName}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: colorPalette.textSecondary }}>
                {isSuper && <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 13, color: colorPalette.primary }} />}
                <Typography noWrap sx={{ fontSize: 11, color: isSuper ? colorPalette.primary : "inherit", fontWeight: isSuper ? 700 : 400 }}>
                  {ROLE_LABEL[role]}
                </Typography>
              </Box>
            </Box>
            <Tooltip title="비밀번호 변경">
              <IconButton size="small" onClick={onChangePassword} sx={{ color: colorPalette.textSecondary }}>
                <LockResetOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="로그아웃">
              <IconButton
                size="small"
                onClick={onLogout}
                sx={{ color: colorPalette.textSecondary, "&:hover": { color: colorPalette.error } }}
              >
                <LogoutOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};

/** 상단 헤더 — 경로 + 현재 메뉴 제목 / 권한 미리보기 전환 + 오늘 날짜 */
const SampleHeader = ({ scopeLabel, menu, role, onRoleChange, sidebarCollapsed, onToggleSidebar, onOpenGuide }) => (
  <Box
    component="header"
    sx={{
      height: 64,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 2,
      pl: 2,
      pr: 4,
      backgroundColor: colorPalette.white,
      borderBottom: `1px solid ${colorPalette.border}`,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
      <Tooltip title={sidebarCollapsed ? "메뉴 펼치기" : "메뉴 접기"}>
        <IconButton
          onClick={onToggleSidebar}
          sx={{
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: 2,
            border: `1px solid ${colorPalette.border}`,
            color: colorPalette.textSecondary,
            "&:hover": { color: colorPalette.primary, borderColor: colorPalette.primary },
          }}
        >
          {sidebarCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: colorPalette.textSecondary }}>
          <Typography noWrap sx={{ fontSize: 12, color: "inherit" }}>
            {scopeLabel}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
          <Typography noWrap sx={{ fontSize: 12, color: "inherit" }}>
            {menu.label}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: colorPalette.textPrimary, lineHeight: 1.3 }}>
          {menu.label}
        </Typography>
      </Box>
    </Box>

    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
      {/* 샘플 전용: 권한별 화면을 미리 확인하기 위한 전환 버튼 */}
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: colorPalette.textSecondary }}>권한 미리보기</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={role}
        onChange={(_, value) => value && onRoleChange(value)}
        sx={{
          "& .MuiToggleButton-root": {
            px: 1.5,
            py: 0.4,
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            color: colorPalette.textSecondary,
            borderColor: colorPalette.border,
          },
          "& .Mui-selected": {
            color: `${colorPalette.primary} !important`,
            backgroundColor: `${colorPalette.activeBg} !important`,
          },
        }}
      >
        <ToggleButton value={ROLE.SUPER}>{ROLE_LABEL[ROLE.SUPER]}</ToggleButton>
        <ToggleButton value={ROLE.ORG}>{ROLE_LABEL[ROLE.ORG]}</ToggleButton>
      </ToggleButtonGroup>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          height: 32,
          borderRadius: 2,
          border: `1px solid ${colorPalette.border}`,
          color: colorPalette.textSecondary,
        }}
      >
        <CalendarTodayOutlinedIcon sx={{ fontSize: 15 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "inherit" }}>{formatToday()}</Typography>
      </Box>
      {/* SOP 문서 연동 인라인 도움말 — 어느 메뉴에서든 같은 가이드를 열 수 있도록 상단 바 우측에 고정 */}
      <Button
        variant="contained"
        disableElevation
        startIcon={<HelpOutlineIcon sx={{ fontSize: "18px !important" }} />}
        onClick={onOpenGuide}
        sx={{ height: 32, px: 1.5, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
      >
        지표 및 대응 가이드
      </Button>
    </Box>
  </Box>
);

/** 본문 — 콘텐츠가 채워지기 전까지 빈 상태 안내를 표시 */
const MenuContent = ({ menu, scopeLabel, isSystemMenu }) => {
  const { Icon } = menu;
  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 4, display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13, color: colorPalette.textSecondary }}>{menu.description}</Typography>
        {/* 시스템 관리 메뉴는 특정 기관에 종속되지 않으므로 조회 범위를 표시하지 않음 */}
        {!isSystemMenu && (
          <Chip
            icon={<ApartmentOutlinedIcon sx={{ fontSize: "16px !important" }} />}
            label={`조회 범위 · ${scopeLabel}`}
            size="small"
            sx={{
              height: 26,
              fontSize: 12,
              fontWeight: 600,
              color: colorPalette.textPrimary,
              backgroundColor: colorPalette.white,
              border: `1px solid ${colorPalette.border}`,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 320,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          borderRadius: 3,
          border: `1px dashed ${colorPalette.border}`,
          backgroundColor: colorPalette.white,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            backgroundColor: colorPalette.activeBg,
            color: colorPalette.primary,
          }}
        >
          <Icon />
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: colorPalette.textPrimary }}>
          {menu.label} 화면 준비 중입니다
        </Typography>
        <Typography sx={{ fontSize: 12, color: colorPalette.textSecondary }}>
          콘텐츠가 구성되면 이곳에 표시됩니다.
        </Typography>
      </Box>
    </Box>
  );
};

const SampleAdminView = ({ me, onChangePassword, onLogout }) => {
  const [role, setRole] = useState(ROLE.ORG);
  const [menuKey, setMenuKey] = useState(ORG_MENUS[0].key);
  // 슈퍼 관리자가 선택한 기관 사업 (null = 전체 기관)
  const [selectedProject, setSelectedProject] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialCollapsed);

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // 저장 실패 시에도 현재 화면의 접힘 동작에는 영향 없음
      }
      return next;
    });
  };

  // 상단 바 [? 지표 및 대응 가이드] 슬라이드 오버 열림 여부
  const [guideOpen, setGuideOpen] = useState(false);

  // 다른 화면에서 이동해 올 때 함께 넘어오는 참가자 관리 필터 (예: 종합현황 → 잠잠 필터)
  const [participantFilter, setParticipantFilter] = useState(null);

  // 사이드바에서 직접 메뉴를 고르면 이전에 넘어온 필터는 해제
  const handleMenuChange = (nextKey) => {
    setMenuKey(nextKey);
    setParticipantFilter(null);
  };

  // 화면 내부 이동 (예: 종합현황 카드 클릭 → 참가자 관리 + 필터)
  const handleNavigate = (nextKey, params = null) => {
    setMenuKey(nextKey);
    setParticipantFilter(nextKey === "participants" ? params : null);
  };

  const menu = ALL_MENUS.find((m) => m.key === menuKey) ?? ORG_MENUS[0];
  const isSystemMenu = SYSTEM_MENUS.some((m) => m.key === menu.key);

  const scopeLabel =
    role === ROLE.SUPER
      ? isSystemMenu
        ? "시스템 관리"
        : selectedProject
          ? `${selectedProject.organization} · ${selectedProject.name}`
          : "전체 기관"
      : me?.organization || "소속 기관";

  // 종합현황 데이터 조회 범위 — 슈퍼 관리자는 선택한 사업(없으면 전체), 기관 관리자는 본인 code
  const scopeKey =
    role === ROLE.SUPER ? selectedProject?.code ?? "ALL" : me?.code || me?.organization || "ORG";

  const handleRoleChange = (nextRole) => {
    setRole(nextRole);
    // 기관 관리자에게는 시스템 관리 메뉴가 없으므로 첫 화면으로 되돌림
    if (nextRole === ROLE.ORG && isSystemMenu) setMenuKey(ORG_MENUS[0].key);
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    // 기관을 바꾸면 해당 기관의 운영 화면을 보도록 이동
    if (isSystemMenu) setMenuKey(ORG_MENUS[0].key);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", backgroundColor: colorPalette.background }}>
      <SampleSidebar
        me={me}
        role={role}
        menuKey={menu.key}
        onMenuChange={handleMenuChange}
        selectedProject={selectedProject}
        onProjectSelect={handleProjectSelect}
        onChangePassword={onChangePassword}
        onLogout={onLogout}
        collapsed={sidebarCollapsed}
      />

      <Box
        component="main"
        sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", backgroundColor: MAIN_BACKGROUND }}
      >
        <SampleHeader
          scopeLabel={scopeLabel}
          menu={menu}
          role={role}
          onRoleChange={handleRoleChange}
          sidebarCollapsed={sidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
          onOpenGuide={() => setGuideOpen(true)}
        />
        {/* 종합현황·참가자 관리는 메인 영역 전체를 사용해 렌더링, 나머지 메뉴는 준비 중 안내 */}
        {menu.key === "overview" ? (
          <OverviewView scopeKey={scopeKey} onNavigate={handleNavigate} />
        ) : menu.key === "report" ? (
          <ReportView key={scopeKey} scopeKey={scopeKey} />
        ) : menu.key === "participants" ? (
          // 조회 범위나 넘어온 필터가 바뀌면 목록·선택 상태를 새로 시작하도록 key로 리셋
          <ParticipantsView
            key={`${scopeKey}|${JSON.stringify(participantFilter)}`}
            scopeKey={scopeKey}
            filter={participantFilter}
          />
        ) : (
          <MenuContent menu={menu} scopeLabel={scopeLabel} isSystemMenu={isSystemMenu} />
        )}
      </Box>

      <SopGuideDrawer open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Box>
  );
};

export default SampleAdminView;
